import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import { useBrand } from '../../context/BrandContext';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Search,
  Eye,
  EyeOff,
  Package,
  Filter,
  ExternalLink
} from 'lucide-react';

function ProductsTab() {
  const brand = useBrand();
  // Check if referrals/commissions are enabled for this brand
  const isReferralsEnabled = brand?.features?.referrals?.enabled !== false;
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [hiddenProducts, setHiddenProducts] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all, hidden, visible
  const [hideReason, setHideReason] = useState('');
  const [showReasonModal, setShowReasonModal] = useState(null); // productId or null

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all products (admin can see everything)
      const productsResponse = await amplifyApi.getPublishedProducts();

      // Fetch hidden products list
      const hiddenResponse = await amplifyApi.adminGetHiddenProducts();

      if (productsResponse.success !== false) {
        setProducts(productsResponse.products || []);
      }

      if (hiddenResponse.success) {
        const hiddenIds = new Set((hiddenResponse.products || []).map(p => p.productId));
        setHiddenProducts(hiddenIds);
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
    }

    setLoading(false);
  };

  const handleHideProduct = async (productId) => {
    setActionLoading(`hide-${productId}`);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.adminHideProduct(productId, hideReason || null);
    if (response.success) {
      setSuccess(`Product hidden from gallery`);
      setHideReason('');
      setShowReasonModal(null);
      loadProducts();
    } else {
      setError(response.error || 'Failed to hide product');
    }

    setActionLoading(null);
  };

  const handleUnhideProduct = async (productId) => {
    setActionLoading(`unhide-${productId}`);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.adminUnhideProduct(productId);
    if (response.success) {
      setSuccess(`Product is now visible in gallery`);
      loadProducts();
    } else {
      setError(response.error || 'Failed to unhide product');
    }

    setActionLoading(null);
  };

  // Check if product is hidden
  const isProductHidden = (productId) => {
    return hiddenProducts.has(productId);
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    // Search filter
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query ||
      product.title?.toLowerCase().includes(query) ||
      product.id?.toLowerCase().includes(query) ||
      product.ownerEmail?.toLowerCase().includes(query);

    if (!matchesSearch) return false;

    // Status filter
    const isHidden = isProductHidden(product.id);
    switch (filter) {
      case 'hidden':
        return isHidden;
      case 'visible':
        return !isHidden && product.visibility === 'public';
      case 'private':
        return product.visibility === 'private';
      case 'restricted':
        return !product.ownerApproved && product.visibility === 'public';
      default:
        return true;
    }
  });

  // Stats
  const stats = {
    total: products.length,
    public: products.filter(p => p.visibility === 'public').length,
    hidden: Array.from(hiddenProducts).length,
    private: products.filter(p => p.visibility === 'private').length
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">Loading products...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Product Moderation</h2>
          <p className="text-sm text-slate-500 mt-1">
            Hide or unhide products from the public gallery
          </p>
        </div>
        <button
          onClick={loadProducts}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Package className="w-4 h-4" />
            <span className="text-xs">Total Products</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <Eye className="w-4 h-4" />
            <span className="text-xs">Public</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{stats.public}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-600 mb-1">
            <EyeOff className="w-4 h-4" />
            <span className="text-xs">Hidden by Admin</span>
          </div>
          <p className="text-2xl font-bold text-red-700">{stats.hidden}</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Filter className="w-4 h-4" />
            <span className="text-xs">Private</span>
          </div>
          <p className="text-2xl font-bold text-slate-700">{stats.private}</p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            &times;
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-500 hover:text-green-700">
            &times;
          </button>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, product ID, or owner email..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'visible', label: 'Visible' },
            { key: 'hidden', label: 'Hidden' },
            { key: 'private', label: 'Private' },
            { key: 'restricted', label: 'Restricted Owner' }
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-2 text-sm rounded-lg transition-colors whitespace-nowrap ${
                filter === f.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Hide Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Hide Product</h3>
            <p className="text-sm text-slate-600 mb-4">
              Optionally provide a reason for hiding this product. This will be visible to the product owner.
            </p>
            <textarea
              value={hideReason}
              onChange={(e) => setHideReason(e.target.value)}
              placeholder="Reason (optional)..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowReasonModal(null);
                  setHideReason('');
                }}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={() => handleHideProduct(showReasonModal)}
                disabled={actionLoading === `hide-${showReasonModal}`}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
              >
                {actionLoading === `hide-${showReasonModal}` && <Loader2 className="w-4 h-4 animate-spin" />}
                Hide Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredProducts.map((product) => {
          const isHidden = isProductHidden(product.id);
          const defaultImage = product.images?.[product.defaultMockupIndex || 0]?.src || product.mockupUrl;

          return (
            <div
              key={product.id}
              className={`bg-white border rounded-lg overflow-hidden ${
                isHidden ? 'border-red-200 bg-red-50/30' : 'border-slate-200'
              }`}
            >
              {/* Product Image */}
              <div className="relative aspect-square bg-slate-100">
                {defaultImage ? (
                  <img
                    src={defaultImage}
                    alt={product.title}
                    className={`w-full h-full object-cover ${isHidden ? 'opacity-50' : ''}`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <Package className="w-12 h-12" />
                  </div>
                )}

                {/* Status Badges */}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  {isHidden && (
                    <span className="px-2 py-1 text-xs font-medium bg-red-500 text-white rounded">
                      Hidden
                    </span>
                  )}
                  {product.visibility === 'private' && (
                    <span className="px-2 py-1 text-xs font-medium bg-slate-500 text-white rounded">
                      Private
                    </span>
                  )}
                  {!product.ownerApproved && product.visibility === 'public' && (
                    <span className="px-2 py-1 text-xs font-medium bg-amber-500 text-white rounded">
                      Owner Restricted
                    </span>
                  )}
                </div>
              </div>

              {/* Product Info */}
              <div className="p-3">
                <h3 className="font-medium text-slate-900 text-sm truncate" title={product.title}>
                  {product.title}
                </h3>

                {/* Price, Margin & Commission Display */}
                {(() => {
                  const variants = product.variants || [];
                  const prices = variants.map(v => v.price).filter(p => p > 0);
                  const costs = variants.map(v => v.cost).filter(c => c > 0);

                  if (prices.length === 0) return null;

                  const minPrice = Math.min(...prices);
                  const maxPrice = Math.max(...prices);
                  const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
                  const priceRange = minPrice === maxPrice
                    ? `$${minPrice.toFixed(2)}`
                    : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;

                  // Calculate margins if we have cost data
                  let grossMargin = null;

                  if (costs.length > 0) {
                    const avgCost = costs.reduce((a, b) => a + b, 0) / costs.length;
                    grossMargin = avgPrice - avgCost;
                  }

                  // Commission calculations only if referrals are enabled
                  let creatorCommission = 0;
                  let referralCommission = 0;
                  let netMarginMin = null;
                  let netMarginMax = null;

                  if (isReferralsEnabled) {
                    const CREATOR_RATE = 0.10; // 10%
                    const REFERRAL_RATE = 0.05; // 5%
                    creatorCommission = avgPrice * CREATOR_RATE;
                    referralCommission = avgPrice * REFERRAL_RATE;
                    const maxCommission = creatorCommission + referralCommission; // Self-referral case (15%)

                    if (grossMargin !== null) {
                      netMarginMin = grossMargin - maxCommission; // Worst case: 15% commission
                      netMarginMax = grossMargin; // Best case: no commission (direct sale)
                    }
                  }

                  return (
                    <div className="mt-1 space-y-1">
                      {/* Price */}
                      <div className="text-xs">
                        <span className="text-green-600 font-medium">{priceRange}</span>
                      </div>

                      {/* Gross Margin */}
                      {grossMargin !== null && (
                        <div className="text-xs text-slate-500">
                          Margin: <span className="font-medium">${grossMargin.toFixed(2)}</span>
                          <span className="text-slate-400"> ({Math.round((grossMargin / (avgPrice - grossMargin)) * 100)}%)</span>
                        </div>
                      )}

                      {/* Commissions - only shown if referrals enabled */}
                      {isReferralsEnabled && (
                        <>
                          <div className="text-xs text-orange-600">
                            Creator: -${creatorCommission.toFixed(2)} (10%)
                            {' | '}
                            Ref: -${referralCommission.toFixed(2)} (5%)
                          </div>

                          {/* Net Margin */}
                          {netMarginMin !== null && (
                            <div className="text-xs">
                              <span className={netMarginMin >= 0 ? 'text-blue-600' : 'text-red-600'}>
                                Net: ${netMarginMin.toFixed(2)} - ${netMarginMax.toFixed(2)}
                                <span className="text-slate-400 ml-1">
                                  ({Math.round((netMarginMin / avgPrice) * 100)}% - {Math.round((netMarginMax / avgPrice) * 100)}%)
                                </span>
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}

                <p className="text-xs text-slate-500 mt-1 truncate" title={product.ownerEmail}>
                  Owner: {product.ownerEmail || 'Unknown'}
                </p>

                {/* Image Source / License */}
                {product.imageSource && product.imageSource !== 'user-upload' && (
                  <p className="text-xs mt-1">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                      product.imageSource === 'world-illustrated' ? 'bg-teal-100 text-teal-700' :
                      product.imageSource === 'rawpixel' ? 'bg-purple-100 text-purple-700' :
                      product.imageSource === 'ai-generated' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {product.imageSource === 'world-illustrated' ? 'WI Licensed' :
                       product.imageSource === 'rawpixel' ? 'Rawpixel' :
                       product.imageSource === 'ai-generated' ? 'AI Generated' :
                       product.imageSource}
                    </span>
                  </p>
                )}

                <p className="text-xs text-slate-400 font-mono mt-1">
                  ID: {product.id?.substring(0, 12)}...
                </p>

                {/* Hidden Reason */}
                {isHidden && product.adminHiddenReason && (
                  <p className="text-xs text-red-600 mt-2 italic">
                    Reason: {product.adminHiddenReason}
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 mt-3">
                  {isHidden ? (
                    <button
                      onClick={() => handleUnhideProduct(product.id)}
                      disabled={actionLoading === `unhide-${product.id}`}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs text-green-600 hover:bg-green-50 border border-green-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {actionLoading === `unhide-${product.id}` ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Eye className="w-3 h-3" />
                      )}
                      Unhide
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowReasonModal(product.id)}
                      disabled={actionLoading === `hide-${product.id}`}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {actionLoading === `hide-${product.id}` ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <EyeOff className="w-3 h-3" />
                      )}
                      Hide
                    </button>
                  )}
                  <a
                    href={`/products/${product.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center p-2 text-slate-500 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
                    title="View Product"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          );
        })}

        {filteredProducts.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No products found</p>
            <p className="text-sm mt-1">
              {searchQuery || filter !== 'all' ? 'Try adjusting your filters' : 'No products have been created yet'}
            </p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-600">
        <strong>Product Visibility Rules:</strong>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li><strong>Visible</strong>: Public products from approved users that aren't admin-hidden</li>
          <li><strong>Hidden</strong>: Products hidden by admin - owner sees "Hidden by Admin" badge</li>
          <li><strong>Private</strong>: Products set to private by owner - only visible to owner</li>
          <li><strong>Restricted Owner</strong>: Public products from non-approved users - hidden from gallery</li>
        </ul>
      </div>
    </div>
  );
}

export default ProductsTab;
